import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '@/store/store-hooks'
import { selectMatterById } from '@/matter/store/selectors'
import type { MatterRead } from '@/matter/store/schemas'
import { matterPaths } from '@/routing/paths'
import ComingSoonCard from '@/components/coming-soon/ComingSoonCard'

export type MatterFeatureGuardProps = {
  /**
   * Permission predicate from `@/matter/utils/matter-permissions` (e.g. canViewTimeline).
   * Receives the matter from the URL; returns whether the current user
   * may use the feature.
   */
  canAccess: (matter: MatterRead | null | undefined) => boolean
  /**
   * Where to send a blocked user instead of the "Coming soon" card. Use for a sub-route
   * the user can't act on but whose parent feature they can still see (e.g. the add-files
   * page when they can view the chronology but not edit it), so they land back on the
   * feature rather than a dead end.
   */
  redirectTo?: (matterId: string) => string
}

/**
 * Route guard for a matter feature, driven by a permission predicate.
 *
 * A user the predicate rejects gets a "Coming soon" card instead of the feature, or a redirect
 * when `redirectTo` is set. Anyone it allows falls through to the route.
 *
 * Relies on MatterLayout (parent) having already loaded matters and validated matter
 * accessibility before this renders, so the matter is present here.
 */
export default function MatterFeatureGuard({ canAccess, redirectTo }: MatterFeatureGuardProps) {
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()
  const matter = useAppSelector((state) => (matterId ? selectMatterById(state, { matterId }) : undefined))

  if (matterId && !canAccess(matter)) {
    if (redirectTo) return <Navigate to={redirectTo(matterId)} replace />
    return <ComingSoonCard actionLabel="Back to Matter" onAction={() => navigate(matterPaths.overview(matterId))} />
  }

  return <Outlet />
}
